import React, { useImperativeHandle, useRef, useState } from "react"
import { COLORS } from "../../../utilities/Constants"

// Choix unique en bulles (maquette « bulles » validée par Jordan le 15.10) :
//  - niveau 1 = choix principaux (Type, types de point) : noir une fois choisi ;
//  - niveau 2 = précisions (réponses du tarif privé) : accent.
// `options` = [[valeur, libellé]] ; `deselectionnable` : 2e clic → null.
// Accessibilité : radiogroup / radio + aria-checked, tabindex itinérant,
// flèches gauche / droite (et haut / bas) = choix voisin, Entrée → `nextRef`
// s'il est fourni (comme SelectV2), sinon le comportement du bouton.
// Survol et focus clavier en state (l'inline n'a pas :hover / :focus-visible).
// La ref expose focus() : la bulle choisie (sinon la première).

function focusTarget(ref) {
	if (!ref) return
	const node = ref.current !== undefined ? ref.current : ref
	if (node && typeof node.focus === "function") node.focus()
}

const TAILLES = {
	1: {
		height: 36,
		padding: "0 14px",
		borderRadius: 10,
		fontSize: 13.5,
		fontWeight: 600,
	},
	2: {
		height: 28,
		padding: "0 11px",
		borderRadius: 8,
		fontSize: 12.5,
		fontWeight: 500,
	},
}

export const styleBulle = (niveau, choisie, survol, focusClavier, disabled) => {
	const couleurs = choisie
		? niveau === 1
			? {
					backgroundColor: COLORS.text,
					borderColor: COLORS.text,
					color: "#fff",
				}
			: {
					backgroundColor: COLORS.accentLight,
					borderColor: COLORS.accent,
					color: COLORS.accent,
				}
		: {
				backgroundColor: survol ? COLORS.bg : COLORS.surface,
				borderColor: survol ? COLORS.borderStrong : COLORS.border,
				color: survol ? COLORS.text : COLORS.textMuted,
			}
	return {
		display: "inline-flex",
		alignItems: "center",
		boxSizing: "border-box",
		border: "1px solid",
		fontFamily: "inherit",
		whiteSpace: "nowrap",
		cursor: disabled ? "not-allowed" : "pointer",
		opacity: disabled ? 0.55 : 1,
		outline: focusClavier ? `2px solid ${COLORS.accent}` : "none",
		outlineOffset: 2,
		...TAILLES[niveau === 2 ? 2 : 1],
		...couleurs,
	}
}

const ChoixBulles = React.forwardRef(
	(
		{
			options = [],
			valeur = null,
			onChange,
			niveau = 1,
			deselectionnable = false,
			disabled = false,
			nextRef = null,
			label = undefined,
			style = undefined,
		},
		ref,
	) => {
		const boutons = useRef([])
		const [survol, setSurvol] = useState(null)
		const [focus, setFocus] = useState(null)
		const [clavier, setClavier] = useState(false)
		const iChoisi = options.findIndex(([v]) => v === valeur)
		const iTab = iChoisi >= 0 ? iChoisi : 0

		useImperativeHandle(ref, () => ({
			focus: () => {
				const b = boutons.current[iTab]
				if (b) b.focus()
			},
		}))

		const choisir = (v) => {
			if (disabled || !onChange) return
			if (deselectionnable && v === valeur) onChange(null)
			else onChange(v)
		}

		const onKeyDown = (e, i) => {
			setClavier(true)
			const pas = {
				ArrowRight: 1,
				ArrowDown: 1,
				ArrowLeft: -1,
				ArrowUp: -1,
			}[e.key]
			if (pas && options.length) {
				e.preventDefault()
				const j = (i + pas + options.length) % options.length
				if (!disabled && onChange) onChange(options[j][0])
				const b = boutons.current[j]
				if (b) b.focus()
				return
			}
			if (e.key === "Enter" && nextRef) {
				e.preventDefault()
				focusTarget(nextRef)
			}
		}

		return (
			<div
				role="radiogroup"
				aria-label={label}
				aria-disabled={disabled || undefined}
				style={{ display: "flex", flexWrap: "wrap", gap: 6, ...style }}
			>
				{options.map(([v, libelle], i) => {
					const choisie = i === iChoisi
					return (
						<button
							key={String(v)}
							ref={(b) => (boutons.current[i] = b)}
							type="button"
							role="radio"
							aria-checked={choisie}
							tabIndex={i === iTab ? 0 : -1}
							disabled={disabled}
							onClick={() => choisir(v)}
							onKeyDown={(e) => onKeyDown(e, i)}
							onMouseDown={() => setClavier(false)}
							onMouseEnter={() => setSurvol(i)}
							onMouseLeave={() => setSurvol(null)}
							onFocus={() => setFocus(i)}
							onBlur={() => setFocus(null)}
							style={styleBulle(
								niveau,
								choisie,
								survol === i && !disabled,
								clavier && focus === i,
								disabled,
							)}
						>
							{libelle}
						</button>
					)
				})}
			</div>
		)
	},
)

ChoixBulles.displayName = "ChoixBulles"

export default ChoixBulles
